import { type FormEvent, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, UserPlus } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { api, ApiError } from "../../lib/api";
import { type CreatePlatformUserRequest, type PlatformRole, platformRoleLabels } from "./types";
import "./platformUsers.css";

type Field = "name" | "mobile_number" | "email" | "role";
type FieldErrors = Partial<Record<Field, string>>;

export default function CreatePlatformUserPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [mobile, setMobile] = useState("+91");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<PlatformRole>("TECH_OPS");
  const [errors, setErrors] = useState<FieldErrors>({});
  const [submitError, setSubmitError] = useState("");
  const errorSummary = useRef<HTMLDivElement>(null);

  function showErrors(fields: FieldErrors, message = "Check the details below.") {
    setErrors(fields);
    setSubmitError(message);
    requestAnimationFrame(() => errorSummary.current?.focus());
  }

  const mutation = useMutation({
    mutationFn: (body: CreatePlatformUserRequest) => api.createPlatformUser(body),
    onSuccess: async user => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["platform-users"] }),
        queryClient.invalidateQueries({ queryKey: ["tech-admin-workspace"] }),
        queryClient.invalidateQueries({ queryKey: ["tech-admin-audit"] }),
        queryClient.invalidateQueries({ queryKey: ["tech-ops-users"] }),
        queryClient.invalidateQueries({ queryKey: ["tech-ops-dashboard"] })
      ]);
      navigate("/platform-users", { replace: true, state: {
        success: `${user.name || user.mobile_number} now has ${platformRoleLabels[role]} access and can sign in with their mobile number.`
      } });
    },
    onError: error => {
      const field: Field | undefined = error instanceof ApiError ? ({
        INVALID_MOBILE_NUMBER: "mobile_number",
        PLATFORM_USER_EXISTS: "mobile_number",
        PLATFORM_USER_ACCOUNT_INACTIVE: "mobile_number",
        PLATFORM_USER_NAME_INVALID: "name",
        INVALID_EMAIL: "email",
        PLATFORM_ROLE_REQUIRED: "role"
      } as Record<string, Field>)[error.code] : undefined;
      showErrors(field ? { [field]: error.message } : {}, field ? undefined : error.message);
    }
  });

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (mutation.isPending) return;
    const next: FieldErrors = {};
    if (!name.trim() || name.trim().length > 150) next.name = "Enter a name of up to 150 characters.";
    if (!/^\+[1-9]\d{7,14}$/.test(mobile.trim())) next.mobile_number = "Enter a mobile number with country code, such as +919876543210.";
    if (email.trim() && (email.trim().length > 254 || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()))) next.email = "Enter a valid email address.";
    if (Object.keys(next).length) { showErrors(next); return; }
    setErrors({});
    setSubmitError("");
    mutation.mutate({ name: name.trim(), mobile_number: mobile.trim(), email: email.trim() || undefined, role });
  }

  return <div className="pa-page-stack pa-platform-users">
    <div><Link className="pa-back-link" to="/platform-users"><ArrowLeft size={16} aria-hidden="true" /> Platform users</Link></div>
    <div className="pa-title-row"><div><h1>Add platform user</h1><p>Choose who can access the operations dashboard and what they can manage.</p></div></div>
    <section className="pa-panel pa-platform-user-form-panel" aria-label="New platform user">
      <form className="pa-admin-form" onSubmit={submit} noValidate aria-busy={mutation.isPending}>
        {submitError ? <div ref={errorSummary} className="pa-form-error" role="alert" tabIndex={-1}>
          <p>{submitError}</p>
          {Object.keys(errors).length ? <ul>{Object.entries(errors).map(([field, message]) => <li key={field}><a href={`#platform-${field}`}>{message}</a></li>)}</ul> : null}
        </div> : null}
        <p className="pa-form-note">If this mobile number already has an account, its profile and organization access will be kept. The details below are used for new accounts.</p>
        <div className="pa-platform-user-field"><label htmlFor="platform-name"><span>Name</span></label>
          <input id="platform-name" autoComplete="name" value={name} onChange={event => setName(event.target.value)} maxLength={150} required disabled={mutation.isPending} aria-invalid={Boolean(errors.name)} aria-describedby={errors.name ? "platform-name-error" : undefined} />
          {errors.name ? <small id="platform-name-error" className="pa-platform-field-error">{errors.name}</small> : null}
        </div>
        <div className="pa-platform-user-field"><label htmlFor="platform-mobile_number"><span>Mobile number</span></label>
          <input id="platform-mobile_number" type="tel" autoComplete="tel" value={mobile} onChange={event => setMobile(event.target.value)} maxLength={16} required disabled={mutation.isPending} aria-invalid={Boolean(errors.mobile_number)} aria-describedby={`platform-mobile-hint${errors.mobile_number ? " platform-mobile-error" : ""}`} />
          <small id="platform-mobile-hint" className="pa-form-note">Include the country code. They’ll use this number for OTP login.</small>
          {errors.mobile_number ? <small id="platform-mobile-error" className="pa-platform-field-error">{errors.mobile_number}</small> : null}
        </div>
        <div className="pa-platform-user-field"><label htmlFor="platform-email"><span>Email (optional)</span></label>
          <input id="platform-email" type="email" autoComplete="email" value={email} onChange={event => setEmail(event.target.value)} maxLength={254} disabled={mutation.isPending} aria-invalid={Boolean(errors.email)} aria-describedby={errors.email ? "platform-email-error" : undefined} />
          {errors.email ? <small id="platform-email-error" className="pa-platform-field-error">{errors.email}</small> : null}
        </div>
        <div className="pa-platform-user-field"><label htmlFor="platform-role"><span>Platform role</span></label>
          <select id="platform-role" value={role} onChange={event => setRole(event.target.value as PlatformRole)} disabled={mutation.isPending} aria-describedby={`platform-role-hint${errors.role ? " platform-role-error" : ""}`} aria-invalid={Boolean(errors.role)}>
            <option value="TECH_OPS">Tech Ops</option><option value="TECH_ADMIN">Tech Admin</option>
          </select>
          <small id="platform-role-hint" className="pa-form-note">{role === "TECH_ADMIN" ? "Administrative tools, including creating users and granting platform access." : "Operations dashboard and operational tools."}</small>
          {errors.role ? <small id="platform-role-error" className="pa-platform-field-error">{errors.role}</small> : null}
        </div>
        <div className="pa-platform-user-actions">
          <button type="submit" className="pa-primary-button" disabled={mutation.isPending}><UserPlus size={16} aria-hidden="true" /> {mutation.isPending ? "Adding platform user…" : "Add platform user"}</button>
          <button type="button" className="pa-secondary-button" disabled={mutation.isPending} onClick={() => navigate("/platform-users")}>Cancel</button>
        </div>
      </form>
    </section>
  </div>;
}
